// src/pages/projects/ManageProjectsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaLayerGroup,
  FaBuilding,
  FaCalendarAlt,
  FaInfoCircle,FaFilm,FaUsers,FaCheckCircle,FaTimesCircle,FaClock
} from 'react-icons/fa';

import api, { fetchProjectListApi,fetchRoleListApi,getAppliedRoleListForDrCr } from '../../services/api';
import MetaTags from '../../components/MetaTags';
import DataTable from '../../components/DataTable'; // ✅ Import reusable Table
import './ManageProjectPage.css';

const ManageProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState({
  totalProjects: 0,
  totalRoles: 0,
  totalAuditions: 0,
  selected: 0,
  rejected: 0,
  pending: 0
});

  // ✅ LOAD PROJECTS
  const loadProjects = async () => {
    setLoading(true);
    setError('');
    try {
      // const { data } = await fetchProjectListApi();
      // const mappedProjects = (data.data || []).map(p => ({
      //   id: p.id,
      //   projectName: p.project_name || p.projectName,
      //   projectType: p.project_type || p.projectType,
      //   projectDirectorName: p.director_name || p.projectDirectorName || "N/A",
      //   projectStartDate: p.start_date || p.projectStartDate,
      //   projectEndDate: p.end_date || p.projectEndDate,
      //   status: p.status === "active" ? "Active" : "Expired",
      // }));
      // setProjects(mappedProjects);
     const { data } = await fetchProjectListApi();
      const rolesRes = await fetchRoleListApi();
      const applicationsRes = await getAppliedRoleListForDrCr();

      const rolesData = rolesRes?.data?.data || [];
      const applications = applicationsRes?.data?.data || [];

      const mappedProjects = (data.data || []).map(p => {
        const currentProjectJogetId = String(p.project_joget_id || p.joget_project_id || p.projectId || '');
        
        const projectRoles = rolesData.filter(role => 
          String(role.project_joget_id || role.projectId || role.c_projectid || '') === currentProjectJogetId
        );

        const projectRoleIds = projectRoles.map(role => String(role.joget_role_id || role.roleId || ''));
        const projectApplications = applications.filter(app => 
          projectRoleIds.includes(String(app.joget_role_id || app.c_roleId || app.roleId || ''))
        );

        const getResult = (app) => String(app.result || app.c_result || '').toLowerCase();
        
        const selectedCount = projectApplications.filter(app => getResult(app) === 'selected').length;
        const rejectedCount = projectApplications.filter(app => getResult(app) === 'rejected').length;
        const pendingCount = projectApplications.filter(app => ['pending', 'resubmit', 'applied'].includes(getResult(app))).length;

        return {
          id: p.id,
          projectName: p.project_name || p.projectName,
          projectType: p.project_type || p.projectType,
          projectDirectorName: p.director_name || p.projectDirectorName || "N/A",
          projectStartDate: p.start_date || p.projectStartDate,
          projectEndDate: p.end_date || p.projectEndDate,
          rolesCount: projectRoles.length,
          auditionsCount: projectApplications.length,
          selectedCount,
          rejectedCount,
          pendingCount,
          status: p.status === "active" ? "Active" : "Expired"
        };
      });
      

      setProjects(mappedProjects);

      const stats = mappedProjects.reduce((acc, item) => ({
        totalRoles: acc.totalRoles + (item.rolesCount || 0),
        totalAuditions: acc.totalAuditions + (item.auditionsCount || 0),
        selected: acc.selected + (item.selectedCount || 0),
        rejected: acc.rejected + (item.rejectedCount || 0),
        pending: acc.pending + (item.pendingCount || 0)
      }), { totalRoles: 0, totalAuditions: 0, selected: 0, rejected: 0, pending: 0 });

      setDashboardStats({ totalProjects: mappedProjects.length, ...stats });
    } catch (err) {
      console.error(err);
      setError('Failed to load projects.');
    }
    setLoading(false);
  };
  const formatCamelCase = (text) => {
  if (!text) return "";

  return text
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  useEffect(() => {
    loadProjects();
  }, []);

  // ✅ EDIT PROJECT (Modified to use URL parameter)
  const handleEdit = (projectId) => { // Renamed project_id to projectId for clarity
    if (!projectId) return alert("Project ID not found.");
    navigate(`/dashboard/post-projects/${projectId}`); // Navigate to /dashboard/post-projects/:projectId
  };

  // ✅ DELETE PROJECT
  const handleDelete = async (id) => {
    if (!id) return alert("Invalid Project ID");
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await api.delete(`/projects/${id}`);
        alert('Deleted successfully!');
        loadProjects();
      } catch (err) {
        alert('Delete failed.');
      }
    }
  };

  // ✅ DEFINE COLUMNS FOR TANSTACK TABLE
  const columns = useMemo(() => [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => <span className="text-white opacity-50">{info.row.index + 1}</span>,
      enableSorting: false, 
    },
    {
      accessorKey: 'projectName',
      header: 'Project Title',
      cell: (info) => <span className="text-white fw-bold">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectType',
      header: 'Type',
      cell: (info) => <span className="text-white opacity-75">{formatCamelCase(info.getValue())}</span>,
    },
    {
      accessorKey: 'projectDirectorName',
      header: 'Director',
      cell: (info) => <span className="text-white opacity-75">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectStartDate',
      header: 'Start Date',
      cell: (info) => <span className="small text-white opacity-75">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectEndDate',
      header: 'End Date',
      cell: (info) => <span className="small text-white opacity-75">{info.getValue()}</span>,
    },
    {
      accessorKey: 'rolesCount',
      header: 'Roles',
      cell: (info) => <span className="text-info fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'auditionsCount',
      header: 'Auditions',
      cell: (info) => <span className="text-warning fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'pendingCount',
      header: 'Pending',
      cell: (info) => <span className="text-primary fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'selectedCount',
      header: 'Selected',
      cell: (info) => <span className="text-success fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'rejectedCount',
      header: 'Rejected',
      cell: (info) => <span className="text-danger fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => (
        <Badge bg={info.getValue() === 'Expired' ? 'danger' : 'success'}>
          {info.getValue()}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-center">Actions</div>,
      enableSorting: false, 
      cell: (info) => (
        <div className="d-flex justify-content-center gap-2">
          <Button variant="link" className="p-0 text-info" onClick={() => handleEdit(info.row.original.id)}>
            <FaEdit />
          </Button>
          <Button variant="link" className="p-0 text-danger" onClick={() => handleDelete(info.row.original.id)}>
            <FaTrash />
          </Button>
        </div>
      ),
    }
  ], []);

  // ✅ RENDER MOBILE CARD
  const renderMobileCard = (project) => (
    <div className="project-mobile-card mb-2" key={project.id}>
      <div className="d-flex justify-content-between align-items-start mb-1">
        <h6 className="project-card-title m-0">{project.projectName}</h6>
        <Badge bg={project.status === 'Expired' ? 'danger' : 'success'}>
          {project.status}
        </Badge>
      </div>
      <div className="card-data-grid mt-2">
        <div className="data-item">
          <FaLayerGroup className="text-danger" />
          <span className="lbl">Type:</span>
          <span className="val">{formatCamelCase(project.projectType)}</span>
        </div>
        <div className="data-item">
          <FaInfoCircle className="text-info" />
          <span className="lbl">Status:</span>
          <span className="val">{project.status}</span>
        </div>
        <div className="data-item">
          <FaCalendarAlt className="text-success" />
          <span className="lbl">Start:</span>
          <span className="val">{project.projectStartDate}</span>
        </div>
        <div className="data-item">
          <FaCalendarAlt className="text-warning" />
          <span className="lbl">End:</span>
          <span className="val">{project.projectEndDate}</span>
        </div>
      </div>
      <hr className="compact-hr" />
      <div className="d-flex justify-content-between align-items-center">
        <div className="house-data">
          <FaBuilding className="text-info me-1" style={{ fontSize: '10px' }} />
          <span className="house-name-gold">{project.projectDirectorName}</span>
        </div>
        <div className="d-flex gap-4">
          <FaEdit className="text-info action-icon" onClick={() => handleEdit(project.id)} style={{ cursor: 'pointer' }} />
          <FaTrash className="text-danger action-icon" onClick={() => handleDelete(project.id)} style={{ cursor: 'pointer' }} />
        </div>
      </div>
    </div>
  );

  // ✅ LOADING VIEW
  if (loading) {
    return (
      <div className="text-center p-5 vh-100" >
        <Spinner animation="border" variant="danger" />
        <p className="text-white mt-2">Project Loading...</p>
      </div>
    );
  }

  // ✅ MAIN RENDER
  return (
    <div className="post-project-page p-2 p-md-3 animate-fade-in">
      <MetaTags title="Manage Projects - Dashboard" />

      {/* HEADER */}
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-uppercase fw-bold text-white m-0">
          Manage <span style={{ color: 'var(--primary-color)' }}>Projects</span>
        </h5>
        
        <Button
          as={Link}
          to="/dashboard/post-projects"
          variant="danger"
          className="btn-compact-header btn-sm fw-bold"
        >
          <FaPlus className="me-1" /> New Project
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">
          {error}
        </Alert>
      )}
      <div className="roles-kpi-grid">
        {[
          { title: "Total Projects", value: dashboardStats.totalProjects, icon: <FaFilm />, trend: "Live Projects" },
          { title: "Total Roles", value: dashboardStats.totalRoles, icon: <FaLayerGroup />, trend: "Active" },
          { title: "Total Auditions", value: dashboardStats.totalAuditions, icon: <FaUsers />, trend: "Applications" },
          { title: "Selected", value: dashboardStats.selected, icon: <FaCheckCircle />, trend: "Selected" },
          { title: "Rejected", value: dashboardStats.rejected, icon: <FaTimesCircle />, trend: "Rejected" },
          { title: "Pending", value: dashboardStats.pending, icon: <FaClock />, trend: "Waiting" }
        ].map((item, index) => (
          <div className="roles-kpi-card" key={index}>
            <div className="roles-kpi-top">
              <div className="roles-kpi-icon">{item.icon}</div>
              <div className="roles-kpi-trend">{item.trend}</div>
            </div>
            <div className="roles-kpi-bottom">
              <p className="roles-kpi-label">{item.title}</p>
              <h3 className="roles-kpi-value">{item.value}</h3>
            </div>
          </div>
        ))}

        </div>

      {/* REUSABLE TANSTACK DATA TABLE */}
      <DataTable 
        columns={columns} 
        data={projects} 
        renderMobileItem={renderMobileCard} 
        searchPlaceholder="Search projects or directors..." 
        className="form-control bg-dark text-white border-secondary w-100 w-md-auto"
      />

    </div>
  );
};

export default ManageProjectsPage;